import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import UploadIcon from '@assets/images/icons/upload-v2.svg'
import { useDispatch } from 'react-redux'
import { toggleUploadsOverlay } from '@/redux/ducks/document/upload'
import PendingUploadStatus from '@/modules/layout/components/menuV2/upload-status/pending-upload'
import CalculatingUploadStatus from '@/modules/layout/components/menuV2/upload-status/calculating-upload'
import SimplePopper from '@/modules/base/components/simple-popper/simple-popper'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import lang from '@/lang'
import { useAppSelector } from '@/redux/store'
import customDecodeUri from '@/utils/customDecodeUri'
import ThemeInterface from '@/theme-interface'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bellIcon: {
    position: 'relative',
    height: 20,
    '& path': {
      fill: 'var(--color-menu-icon)',
    },
    '&:hover path': {
      fill: theme.primary,
    },
  },
  active: ({
    fill: theme.primary,
    '& $bellIcon path': {
      fill: theme.primary,
    },
  }),
  hideActiveIcon: {
    '&:after': {
      display: 'none',
    },
  },
  iconWrapper: {
    position: 'relative',
  },
  badge: {
    borderRadius: '50%',
    color: theme.white,
    backgroundColor: theme.error,
    fontSize: 10,
    textAlign: 'center',
    right: 0,
    top: 2,
    height: 15,
    width: 15,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 1,
    position: 'absolute',
    transform: 'translate3d(50%, -50%, 0)',
  },
  [theme.devices.mobile]: {
    container: {
      flexDirection: 'row',
      margin: 0,
      width: 'auto',
    },
    iconWrapper: {
      marginRight: 30,
    },
    newsIcon: {
      marginRight: 70,
    },
    bellIcon: {
      height: 20,
    },
  },
  uploadStatusPopper: {
    marginTop: 10,
    width: 434,
    maxHeight: 480,
    padding: CssHelper.px(30, 20),
    borderRadius: 5,
    background: theme.white,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  perfectScrollbar: {
    padding: CssHelper.px(0, 20),
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 365,
  },
  borderTop: {
    borderTop: theme.border,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '0 20px 10px',
  },
  title: {
    fontWeight: theme.fontWeights.semiBold,
    fontSize: 18,
    marginRight: 10,
  },
  subTitle: {
    color: theme.grey1,
    fontSize: 12,
  },
}))

interface NotificationButtonProps {
  iconSize?: { width: number, height: number },
  hideActiveIcon?: boolean,
  styles?: { root?: string, badge?: string },
  buttonRef,
}

const UploadStatusButton = (props: NotificationButtonProps) => {
  const dispatch = useDispatch()
  const {
    iconSize = { width: 20, height: 20 },
    hideActiveIcon,
    styles = { root: null, badge: null },
    buttonRef,
  } = props
  const classes = useStyles()
  const pending = useAppSelector(state => state.document.upload.pending)
  const calculating = useAppSelector(state => state.document.upload.calculating)
  const overlayOpen = useAppSelector(state => state.document.upload.overlayOpen)
  const pendingUploads = pending.filter(({ fileName }) => !calculating.map(({ fileName }) => customDecodeUri(fileName)).includes(customDecodeUri(fileName)))
  const completedCount = pendingUploads.filter(({ finished }) => finished).length
  const count = pendingUploads.filter(({ finished }) => !finished).length + calculating.length
  const fullCount = pendingUploads.length + calculating.length

  const className = clsx(
    classes.container,
    classes.iconWrapper,
    classes.newsIcon,
    styles?.root && styles.root,
    overlayOpen && classes.active,
    hideActiveIcon && classes.hideActiveIcon,
  )
  const badgeClassName = clsx(
    classes.badge,
    styles?.badge && styles.badge,
  )

  const pendingComponents = useMemo(() =>
    pendingUploads.map((upload, i) => (
      <div key={`upload${i}` } className={clsx(i > 0 && classes.borderTop)}>
        <PendingUploadStatus key={`pendingUpload#${i}`} uploadToken={upload.uploadToken} />
      </div>)),
  [pendingUploads.length])
  const calculatingComponents = useMemo(() =>
    calculating.map((calculating, i) => (
      <div key={`calculating${i}` } className={clsx((i > 0 || pendingUploads.length) && classes.borderTop)}>
        <CalculatingUploadStatus key={`pendingUpload#${i}`} uuid={calculating.uuid} />
      </div>)),
  [calculating.length])

  return (
    <a
      className={className}
      onClick={() => dispatch(toggleUploadsOverlay(!overlayOpen))}
      data-qs-id='upload-status-button' >
      <span className={classes.bellIcon}>
        {count > 0 && <span className={badgeClassName}>{count}</span>}
        <SVGIcon
          width={`${iconSize.width}px`}
          height={`${iconSize.height}px`}
          svg={UploadIcon}
        />
      </span>
      <SimplePopper open={overlayOpen} onClose={() => dispatch(toggleUploadsOverlay(false)) }
        anchorEl={buttonRef?.current} popperOptions={{ modifiers: { offset: { offset: '-110, 13' } } }} >
        <div onClick={(e) => e.stopPropagation()} className={classes.uploadStatusPopper}>
          <div className={classes.header}>
            <span className={classes.title}>{lang.documents.upload.status.title}</span>
            <span className={classes.subTitle}>{lang.documents.upload.status.progressSubtitle({ completedCount, fullCount })}</span>
          </div>
          <PerfectScrollbar options={{ suppressScrollX: true }} className={clsx(classes.perfectScrollbar)}>
            <div className={classes.content}>
              {pendingComponents}
              {calculatingComponents}
            </div>
          </PerfectScrollbar>
        </div>
      </SimplePopper>
    </a>
  )
}

export default UploadStatusButton
